import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Grid,
  Icon,
  Input,
  Loader,
  Message,
  Table,
} from 'semantic-ui-react';
import MotivationLayout from '../MotivationLayout';
import ConfirmSaveModal from '../ConfirmSaveModal';
import FiltersComponent from '../../../components/FiltersComponent';
import { RangeInput } from '../../../components/FiltersComponent/FilterField';
import TableStats from '../../../components/TableStats';
import {
  createAccrualSettings,
  exportAccrualSettings,
  fetchAccrualSettings,
  updateAccrualSettings,
} from '../api';
import {
  AccrualSettings,
  AccrualSettingsForm,
  defaultPageRequest,
} from '../types';
import SuccessDataSaveNotify from '../../Cards/SuccessDataSaveNotify';
import { downloadBlob, getFileNameFromHeader } from '../../../shared/utils';

type SleepField = 'sleepBonusPoints' | 'sleepBonusMinSales' | 'sleepBonusIdleMonths';
type SleepErrors = Partial<Record<SleepField, string>>;

const emptyForm: AccrualSettingsForm = {
  enabled: true,
  firstSaleMonthPoints: '300',
  motivationEnabled: true,
  sleepBonusEnabled: false,
  sleepBonusPoints: '300',
  sleepBonusMinSales: '2',
  sleepBonusIdleMonths: '3',
};

const numberToField = (value: number | undefined | null, fallback: number) =>
  String(value !== undefined && value !== null ? value : fallback);

const FirstSalePage = () => {
  const [items, setItems] = useState<AccrualSettings[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState('');
  const [selected, setSelected] = useState<AccrualSettings | null>(null);
  const [form, setForm] = useState<AccrualSettingsForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [pointsError, setPointsError] = useState('');
  const [sleepErrors, setSleepErrors] = useState<SleepErrors>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fillForm = (row: AccrualSettings | null) => {
    if (row) {
      setForm({
        enabled: !!row.enabled,
        firstSaleMonthPoints: numberToField(row.firstSaleMonthPoints, 0),
        motivationEnabled: row.motivationEnabled !== false,
        sleepBonusEnabled: !!row.sleepBonusEnabled,
        sleepBonusPoints: numberToField(row.sleepBonusPoints, 300),
        sleepBonusMinSales: numberToField(row.sleepBonusMinSales, 2),
        sleepBonusIdleMonths: numberToField(row.sleepBonusIdleMonths, 3),
      });
    } else {
      setForm(emptyForm);
    }
    setSleepErrors({});
  };

  const load = useCallback((filterStr?: string) => {
    setLoading(true);
    setLoadError('');
    fetchAccrualSettings(defaultPageRequest, filterStr)
      .then(({ items: list, totalElements: total }) => {
        const rows = Array.isArray(list) ? list : [];
        setItems(rows);
        setTotalElements(total || rows.length || 0);
        const first = rows.length > 0 ? rows[0] : null;
        setSelected(first);
        fillForm(first);
      })
      .catch(() => {
        setLoadError('Не удалось загрузить настройки');
        setItems([]);
        setTotalElements(0);
        setSelected(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filters);
  }, [load, filters]);

  const applyFilters = (filterUrl: string) => {
    setFilters(filterUrl || '');
  };

  const selectRow = (row: AccrualSettings) => {
    setSelected(row);
    fillForm(row);
    setPointsError('');
  };

  const startCreate = () => {
    setSelected(null);
    fillForm(null);
    setPointsError('');
  };

  const validate = (): boolean => {
    const points = Number(form.firstSaleMonthPoints);
    if (
      form.firstSaleMonthPoints === '' ||
      Number.isNaN(points) ||
      !Number.isInteger(points)
    ) {
      setPointsError('Укажите целое число баллов');
      return false;
    }
    if (form.enabled && points < 1) {
      setPointsError('При включённом начислении баллы должны быть ≥ 1');
      return false;
    }
    if (!form.enabled && points < 0) {
      setPointsError('Число баллов не может быть отрицательным');
      return false;
    }
    setPointsError('');
    return validateSleep();
  };

  const validateSleep = (): boolean => {
    const errors: SleepErrors = {};
    const check = (field: SleepField, min: number, message: string) => {
      const raw = form[field];
      const value = Number(raw);
      if (raw === '' || Number.isNaN(value) || !Number.isInteger(value) || value < min) {
        errors[field] = message;
      }
    };
    check(
      'sleepBonusPoints',
      form.sleepBonusEnabled ? 1 : 0,
      form.sleepBonusEnabled
        ? 'При включённом начислении количество баллов должно быть ≥ 1'
        : 'Целое число ≥ 0'
    );
    check('sleepBonusMinSales', 1, 'Целое число ≥ 1');
    check('sleepBonusIdleMonths', 1, 'Целое число ≥ 1');
    setSleepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const setSleepField = (field: SleepField, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSleepErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const requestSave = () => {
    if (!validate()) {
      return;
    }
    setConfirmOpen(true);
  };

  const doSave = () => {
    const body = {
      enabled: form.enabled,
      firstSaleMonthPoints: Number(form.firstSaleMonthPoints),
      motivationEnabled: form.motivationEnabled,
      sleepBonusEnabled: form.sleepBonusEnabled,
      sleepBonusPoints: Number(form.sleepBonusPoints),
      sleepBonusMinSales: Number(form.sleepBonusMinSales),
      sleepBonusIdleMonths: Number(form.sleepBonusIdleMonths),
    };
    setSaving(true);
    const request =
      selected && selected.id !== undefined
        ? updateAccrualSettings(selected.id, body)
        : createAccrualSettings(body);

    request
      .then(() => {
        setConfirmOpen(false);
        SuccessDataSaveNotify('Изменения сохранены');
        load(filters);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const onExport = () => {
    setExporting(true);
    exportAccrualSettings()
      .then((response) => {
        const fileName = getFileNameFromHeader(
          response.headers['content-disposition'] || '',
          'accrual-settings.csv'
        );
        const blob = new Blob([response.data]);
        downloadBlob(blob, fileName);
      })
      .catch(() => {})
      .finally(() => setExporting(false));
  };

  return (
    <MotivationLayout activeTab="first-sale" title="Первая продажа месяца">
      <Grid stackable>
        <Grid.Row>
          <Grid.Column className="table-page__stats">
            <TableStats totalElements={totalElements} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            <FiltersComponent
              applyFilters={applyFilters}
              totalElements={totalElements}
              render={() => (
                <React.Fragment>
                  <Button
                    className="filters__button"
                    labelPosition="right"
                    icon
                    loading={exporting}
                    disabled={exporting}
                    onClick={onExport}
                    type="button"
                  >
                    Экспорт CSV
                    <Icon name="download" />
                  </Button>
                  <Button
                    className="filters__button"
                    labelPosition="right"
                    icon
                    onClick={() => {
                      if (items.length > 0) {
                        selectRow(items[0]);
                      } else {
                        startCreate();
                      }
                    }}
                    type="button"
                  >
                    {items.length > 0 ? 'Изменить настройку' : 'Новая настройка'}
                    <Icon name={items.length > 0 ? 'edit' : 'plus'} />
                  </Button>
                </React.Fragment>
              )}
            >
              <Input type="text" name="id" placeholder="ID настройки" />
              <RangeInput
                type="range"
                name="firstSaleMonthPoints"
                placeholder="Баллы"
              />
              <Checkbox name="enabled" label="Только включённые" />
            </FiltersComponent>
          </Grid.Column>
          <Grid.Column width={13}>
            {loading ? (
              <Loader active inline="centered" />
            ) : loadError ? (
              <Message negative>
                <Message.Header>{loadError}</Message.Header>
                <Button
                  size="small"
                  onClick={() => load(filters)}
                  style={{ marginTop: 8 }}
                >
                  Повторить
                </Button>
              </Message>
            ) : (
              <React.Fragment>
                <Table celled compact selectable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>ID</Table.HeaderCell>
                      <Table.HeaderCell>Мотивация</Table.HeaderCell>
                      <Table.HeaderCell>Первая продажа</Table.HeaderCell>
                      <Table.HeaderCell>Баллы</Table.HeaderCell>
                      <Table.HeaderCell>Возврат в программу</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {items.length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan={5} textAlign="center">
                          Настройки не найдены — заполните поля ниже и сохраните
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      items.map((row) => (
                        <Table.Row
                          key={row.id}
                          active={!!selected && selected.id === row.id}
                          onClick={() => selectRow(row)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Table.Cell>{row.id}</Table.Cell>
                          <Table.Cell>
                            {row.motivationEnabled === false ? 'Выключена' : 'Включена'}
                          </Table.Cell>
                          <Table.Cell>{row.enabled ? 'Да' : 'Нет'}</Table.Cell>
                          <Table.Cell>{row.firstSaleMonthPoints}</Table.Cell>
                          <Table.Cell>
                            {row.sleepBonusEnabled
                              ? `Да, ${numberToField(row.sleepBonusPoints, 300)}`
                              : 'Нет'}
                          </Table.Cell>
                        </Table.Row>
                      ))
                    )}
                  </Table.Body>
                </Table>

                <Form
                  className="motivation-page__form"
                  style={{ marginTop: 24 }}
                  onSubmit={(e) => e.preventDefault()}
                >
                  <h3 className="motivation-page__form-title">
                    {selected && selected.id !== undefined
                      ? `Изменить настройку #${selected.id}`
                      : items.length > 0
                      ? 'Изменить настройку'
                      : 'Новая настройка'}
                  </h3>
                  <Form.Field>
                    <Checkbox
                      toggle
                      label="Мотивация включена"
                      checked={form.motivationEnabled}
                      onChange={(_e, { checked }) =>
                        setForm((prev) => ({
                          ...prev,
                          motivationEnabled: !!checked,
                        }))
                      }
                    />
                  </Form.Field>
                  <p className="motivation-page__hint">
                    Общий выключатель. Когда он выключен, не начисляются баллы
                    за первую продажу, миссии, повышение категории и возврат в
                    программу, а категории продавцов не пересчитываются.
                  </p>
                  <Form.Field>
                    <Checkbox
                      toggle
                      label="Начисление баллов за первую продажу включено"
                      checked={form.enabled}
                      onChange={(_e, { checked }) =>
                        setForm((prev) => ({
                          ...prev,
                          enabled: !!checked,
                        }))
                      }
                    />
                  </Form.Field>
                  <Form.Input
                    label="Баллы за первую продажу месяца"
                    type="number"
                    min={0}
                    step={1}
                    value={form.firstSaleMonthPoints}
                    error={pointsError || false}
                    onChange={(_e, { value }) => {
                      setForm((prev) => ({
                        ...prev,
                        firstSaleMonthPoints: String(value),
                      }));
                      setPointsError('');
                    }}
                  />
                  <p className="motivation-page__hint">
                    Баллы начисляются один раз за календарный месяц при первой
                    успешной продаже продавца.
                  </p>

                  <h3
                    className="motivation-page__form-title"
                    style={{ marginTop: 24 }}
                  >
                    Баллы за возврат в программу
                  </h3>
                  <Form.Field>
                    <Checkbox
                      toggle
                      label="Начисление баллов за возврат включено"
                      checked={form.sleepBonusEnabled}
                      onChange={(_e, { checked }) => {
                        setForm((prev) => ({
                          ...prev,
                          sleepBonusEnabled: !!checked,
                        }));
                        setSleepErrors({});
                      }}
                    />
                  </Form.Field>
                  <Form.Group widths="equal">
                    <Form.Input
                      label="Баллы"
                      type="number"
                      min={0}
                      step={1}
                      value={form.sleepBonusPoints}
                      error={sleepErrors.sleepBonusPoints || false}
                      onChange={(_e, { value }) =>
                        setSleepField('sleepBonusPoints', String(value))
                      }
                    />
                    <Form.Input
                      label="Продаж в месяце возврата, не меньше"
                      type="number"
                      min={1}
                      step={1}
                      value={form.sleepBonusMinSales}
                      error={sleepErrors.sleepBonusMinSales || false}
                      onChange={(_e, { value }) =>
                        setSleepField('sleepBonusMinSales', String(value))
                      }
                    />
                    <Form.Input
                      label="Полных месяцев без продаж"
                      type="number"
                      min={1}
                      step={1}
                      value={form.sleepBonusIdleMonths}
                      error={sleepErrors.sleepBonusIdleMonths || false}
                      onChange={(_e, { value }) =>
                        setSleepField('sleepBonusIdleMonths', String(value))
                      }
                    />
                  </Form.Group>
                  <p className="motivation-page__hint">
                    Баллы начисляются продавцу, у которого не было успешных продаж
                    указанное число полных месяцев подряд, а в текущем месяце их
                    стало не меньше заданного. Продавцы, пришедшие в программу
                    позже начала этого периода, баллы не получают. Выплачиваются
                    один раз за месяц возврата.
                  </p>
                  <Button
                    primary
                    type="button"
                    onClick={requestSave}
                    disabled={saving}
                    style={{ marginTop: 16 }}
                  >
                    Сохранить
                  </Button>
                </Form>
              </React.Fragment>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <ConfirmSaveModal
        open={confirmOpen}
        loading={saving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doSave}
      />
    </MotivationLayout>
  );
};

export default FirstSalePage;
